import { Colors } from "../../../../utils/colors";
import {
  ONE_DAY_IN_MS,
  ONE_HOUR_IN_MS,
  ONE_MINUTE_IN_MS,
} from "@markit/common.utils";
import { useTheme } from "../../../../hooks/useTheme";
import {
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  ScheduleTextInfo,
} from "@markit/common.types";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { Icon } from "@iconify/react";
import NumberInput from "../../../NumberInput";
import { useMemo, useState } from "react";
import { useOnMount } from "../../../../hooks/useOnMount";

type MassTextRelativeSendTimeProps = {
  scheduleTextInfo: ScheduleTextInfo;
  setScheduleTextInfo: (scheduleTextInfo: ScheduleTextInfo) => void;
  campaignId: string;
};

export const MassTextRelativeSendTime = (
  props: MassTextRelativeSendTimeProps
) => {
  const { scheduleTextInfo, setScheduleTextInfo, campaignId } = props;
  const [days, setDays] = useState<number | undefined>(0);
  const [hours, setHours] = useState<number | undefined>(0);
  const [minutes, setMinutes] = useState<number | undefined>(0);
  const { theme } = useTheme();

  // Update local state when scheduleTextInfo changes
  useOnMount(() => {
    const totalMs = Math.abs(scheduleTextInfo.sentAtOffset);
    const daysValue = Math.floor(totalMs / ONE_DAY_IN_MS);
    const hoursValue = Math.floor((totalMs % ONE_DAY_IN_MS) / ONE_HOUR_IN_MS);
    const minutesValue = Math.floor(
      (totalMs % ONE_HOUR_IN_MS) / ONE_MINUTE_IN_MS
    );

    setDays(daysValue);
    setHours(hoursValue);
    setMinutes(minutesValue);
  });

  const isEventStartDelay = useMemo(
    () => campaignId === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
    [campaignId]
  );

  const maximumDays = useMemo(
    () => (isEventStartDelay ? 365 : 29),
    [isEventStartDelay]
  );

  const updateSentAtOffset = (
    newDays: number,
    newHours: number,
    newMinutes: number
  ) => {
    const multiplier = scheduleTextInfo.sentAtOffset < 0 ? -1 : 1;
    const totalMs =
      newDays * ONE_DAY_IN_MS +
      newHours * ONE_HOUR_IN_MS +
      newMinutes * ONE_MINUTE_IN_MS;
    // NOTE: Prevent saving with zero offset for default event automations, otherwise the MassText object will think it's a mass text if sentAtOffset is 0.
    // Enforce the minimum offset of 5 minutes. Possibly change this in the future.
    if (totalMs < 5 * ONE_MINUTE_IN_MS) {
      setMinutes(5);
      setScheduleTextInfo({
        ...scheduleTextInfo,
        sentAtOffset: 5 * ONE_MINUTE_IN_MS * multiplier,
      });
    } else {
      setScheduleTextInfo({
        ...scheduleTextInfo,
        sentAtOffset: totalMs * multiplier,
      });
    }
  };

  const styles = {
    inputContainer: { width: 102, height: 50 },
    sectionContainer: {
      backgroundColor: Colors.WHITE1,
      paddingBlock: 14,
    },
    toggleItemContainer: {
      flex: 1,
      backgroundColor: Colors.WHITE,
      borderRadius: 14,
      padding: 14,
      display: "flex",
    },
  };

  return (
    <StandardBorderedContainer
      containerStyles={styles.sectionContainer}
      className="ColumnNormal"
    >
      <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
        <span className="bodyMedium">
          {isEventStartDelay ? "Relative Send Time" : "Set Time Delay"}
        </span>
        <span className="smallBodySubtext">
          {isEventStartDelay ? (
            "Set your text to send before or after your event with your desired relative time offset."
          ) : (
            <>
              <span>
                Set the amount of time you want to wait before sending the next
                text.
              </span>
              <br />
              <span>
                Edits to auto texts will not be applied to people who have
                already entered this flow.
              </span>
            </>
          )}
        </span>
      </div>
      <HorizontalDivider altMargin={18} />
      <div className="ColumnNormal" style={{ gap: 18, paddingInline: 14 }}>
        <div
          className={isEventStartDelay ? "AlignedRowSpaced" : "AlignedRow"}
          style={{ gap: isEventStartDelay ? 0 : 14 }}
        >
          {isEventStartDelay ? (
            <span className="bodyMedium">Time Offset</span>
          ) : null}
          <div style={styles.inputContainer}>
            <NumberInput
              min={0}
              max={maximumDays}
              value={days !== undefined ? Math.abs(days) : undefined}
              onChange={(value) => {
                const newDays = value ?? 0;
                const roundedDays = Math.min(newDays, maximumDays);
                setDays(value !== undefined ? roundedDays : undefined);
                updateSentAtOffset(roundedDays, hours ?? 0, minutes ?? 0);
              }}
              containerStyles={{ backgroundColor: Colors.WHITE }}
              prefix={<span className="smallBodySubtext">Days</span>}
            />
          </div>
          <div style={styles.inputContainer}>
            <NumberInput
              min={0}
              max={23}
              value={hours !== undefined ? Math.abs(hours) : undefined}
              onChange={(value) => {
                const newHours = value ?? 0;
                const roundedHours = Math.min(newHours, 23);
                setHours(value !== undefined ? roundedHours : undefined);
                updateSentAtOffset(days ?? 0, roundedHours, minutes ?? 0);
              }}
              containerStyles={{ backgroundColor: Colors.WHITE }}
              prefix={<span className="smallBodySubtext">Hrs</span>}
            />
          </div>
          <div style={styles.inputContainer}>
            <NumberInput
              min={0}
              max={59}
              value={minutes !== undefined ? Math.abs(minutes) : undefined}
              onChange={(value) => {
                const newMinutes = value ?? 0;
                const roundedMinutes = Math.min(newMinutes, 59);
                setMinutes(value !== undefined ? roundedMinutes : undefined);
                updateSentAtOffset(days ?? 0, hours ?? 0, roundedMinutes);
              }}
              containerStyles={{ backgroundColor: Colors.WHITE }}
              prefix={<span className="smallBodySubtext">Mins</span>}
              altErrorMessage={
                !days && !hours && minutes && minutes <= 5
                  ? "min. 5"
                  : undefined
              }
            />
          </div>
        </div>
        {isEventStartDelay ? (
          <div
            className="AlignedRowSpaced"
            style={{
              gap: 14,
              opacity: scheduleTextInfo.sentAtOffset === 0 ? 0.3 : 1,
            }}
          >
            <StandardBorderedContainer
              containerStyles={{
                ...styles.toggleItemContainer,
                border:
                  scheduleTextInfo.sentAtOffset < 0
                    ? "2px solid #000"
                    : `1.5px solid ${theme.DividerColor.borderColor}`,
              }}
              className="AlignedRowSpacedSelect"
              onPress={() =>
                setScheduleTextInfo({
                  ...scheduleTextInfo,
                  sentAtOffset: -Math.abs(scheduleTextInfo.sentAtOffset),
                })
              }
            >
              <span className="bodyMedium">Before Event Start</span>
              {scheduleTextInfo.sentAtOffset < 0 ? (
                <Icon
                  icon="ion:checkmark-circle"
                  height={25}
                  color={Colors.BLACK}
                />
              ) : null}
            </StandardBorderedContainer>
            <StandardBorderedContainer
              containerStyles={{
                ...styles.toggleItemContainer,
                border:
                  scheduleTextInfo.sentAtOffset > 0
                    ? "2px solid #000"
                    : `1.5px solid ${theme.DividerColor.borderColor}`,
              }}
              className="AlignedRowSpacedSelect"
              onPress={() =>
                setScheduleTextInfo({
                  ...scheduleTextInfo,
                  sentAtOffset: Math.abs(scheduleTextInfo.sentAtOffset),
                })
              }
            >
              <span className="bodyMedium">After Event Start</span>
              {scheduleTextInfo.sentAtOffset > 0 ? (
                <Icon
                  icon="ion:checkmark-circle"
                  height={25}
                  color={Colors.BLACK}
                />
              ) : null}
            </StandardBorderedContainer>
          </div>
        ) : null}
      </div>
    </StandardBorderedContainer>
  );
};
